import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseApiPath } from '@/app/common/api/config';
import customFetchBase from '@/app/common/utils/customFetchBase';

type DomainsQueryBody = {
  page: number;
  perPage: number;
  search_text?: string;
};

type DomainMemberBody = {
  domain_slug: string;
  [key: string]: unknown;
};

const domainsApi = createApi({
  reducerPath: 'domains',
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['Domains', 'DomainsCount', 'DomainUsers', 'UserInfo'],
  endpoints: build => ({
    getDomains: build.query<unknown, DomainsQueryBody>({
      query: body => ({
        url: `/go_handler/v2/domains/get`,
        method: 'POST',
        body
      }),
      providesTags: ['Domains']
    }),
    getDomainsCount: build.query<unknown, DomainsQueryBody>({
      query: body => ({
        url: `/go_handler/v2/domains/count2`,
        method: 'POST',
        body
      }),
      providesTags: ['DomainsCount']
    }),
    getUsersByDomain: build.query<unknown, string>({
      query: domain_slug => ({
        url: `/go_handler/v2/domains/get_members`,
        method: 'POST',
        body: { domain_slug }
      }),
      providesTags: ['DomainUsers']
    }),
    removeUser: build.mutation<unknown, DomainMemberBody>({
      query: body => ({
        url: `/go_handler/v2/domains/delete_member`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DomainUsers', 'Domains', 'DomainsCount']
    }),
    addUser: build.mutation<unknown, DomainMemberBody>({
      query: body => ({
        url: `/go_handler/v2/domains/add_member`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DomainUsers', 'Domains', 'DomainsCount']
    }),
    removeDomain: build.mutation<unknown, string>({
      query: domain_slug => ({
        url: `/go_handler/v2/domains/delete`,
        method: 'POST',
        body: { domain_slug }
      }),
      invalidatesTags: ['DomainsCount', 'Domains', 'UserInfo']
    }),
    removeAccess: build.mutation<unknown, string>({
      query: domain_slug => ({
        url: `/go_handler/v2/domains/delete_access`,
        method: 'POST',
        body: { domain_slug }
      }),
      invalidatesTags: ['DomainsCount', 'Domains']
    }),
    createDomain: build.mutation<unknown, Record<string, unknown>>({
      query: body => ({
        url: `/go_handler/v2/domains/create`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DomainsCount', 'Domains', 'UserInfo']
    })
  })
});

export const {
  useGetDomainsQuery,
  useGetDomainsCountQuery,
  useGetUsersByDomainQuery,
  useCreateDomainMutation,
  useRemoveDomainMutation,
  useRemoveAccessMutation,
  useRemoveUserMutation,
  useAddUserMutation
} = domainsApi;

export default domainsApi;
